import { useState, type FormEvent } from "react";
import { contactEmail } from "@/mocks/site";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_URL = "https://readdy.ai/api/form/daim6b3p14a1h3mr76qg";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    payload.delete("company_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试，或直接发送邮件联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试，或直接发送邮件联系我们。");
    }
  };

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="w-full scroll-mt-20 border-t border-background-200 bg-background-100/60"
    >
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-4 py-14 md:px-8 md:py-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="label-caps text-[11px] text-primary-600">Contact</p>
          <h2
            id="contact-heading"
            className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl"
          >
            留下你的线索或问题
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            如果你手上有更接近一手的信息，或者想提一个还没被好好梳理过的事件，都可以通过下面的表单告诉我们。
            我们更看重可核验的公开链接与原始材料，而不是单一转述。
          </p>
          <ul className="mt-7 space-y-4">
            {[
              {
                icon: "ri-mail-line",
                label: "联系邮箱",
                value: contactEmail,
              },
              { icon: "ri-wechat-line", label: "微信公众号", value: "搜索「15黑料网」" },
              { icon: "ri-time-line", label: "回复时间", value: "工作日 48 小时内" },
            ].map((item) => (
              <li key={item.label} className="flex items-start gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-background-300 bg-background-50 text-primary-600">
                  <i className={`${item.icon} text-lg`} aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">{item.label}</span>
                  <span className="mt-0.5 block text-sm font-medium text-foreground-900">
                    {item.value}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="contact-name"
                  className="mb-2 block text-xs font-medium text-foreground-700"
                >
                  称呼
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="怎么称呼你"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="mb-2 block text-xs font-medium text-foreground-700"
                >
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-5">
              <label
                htmlFor="contact-type"
                className="mb-2 block text-xs font-medium text-foreground-700"
              >
                事由类型
              </label>
              <select
                id="contact-type"
                name="subject"
                defaultValue="提供线索"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="提供线索">提供线索</option>
                <option value="内容更正">内容更正</option>
                <option value="投稿意向">投稿意向</option>
                <option value="合作与授权">合作与授权</option>
                <option value="其他问题">其他问题</option>
              </select>
            </div>

            <div className="mt-5">
              <label
                htmlFor="contact-message"
                className="mb-2 block text-xs font-medium text-foreground-700"
              >
                具体内容（500 字以内）
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述事由，并尽量附上可核验的公开链接或材料来源。"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-extra-field"
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              {status === "submitting" ? "提交中…" : "提交"}
            </button>

            {status === "success" ? (
              <p
                role="status"
                className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-3 text-sm text-accent-900"
              >
                <i className="ri-check-line" aria-hidden="true" />
                已经收到，感谢你的补充。我们会尽快查看并回复。
              </p>
            ) : null}

            {status === "error" ? (
              <p
                role="alert"
                className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-3.5 py-3 text-sm text-primary-800"
              >
                <i className="ri-error-warning-line" aria-hidden="true" />
                {formError}
              </p>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
}